import { cn } from "@gettactic/helpers";
import { ChevronDown } from "lucide-react";
import React from "react";
import { ImageThumbnail } from "../../data-display/ImageThumbnail";
import { useFormControlInputA11y } from "../FormControlWrapper/FormControlWrapperProvider";
import { ImageOption } from "./ImageOptionPicker.types";

export interface ImageOptionPickerTriggerProps {
  selectedOptions: ImageOption[];
  isOpen: boolean;
  isDisabled?: boolean;
  placeholder: string;
  /** Name of the field, announced before the selection by screen readers. */
  label?: string;
}

/**
 * The picker's closed state: the selected option's thumbnail and label, or the
 * placeholder. Rendered as the popover trigger, so it forwards the ref and any
 * props Radix injects.
 */
export const ImageOptionPickerTrigger = React.forwardRef<
  HTMLButtonElement,
  ImageOptionPickerTriggerProps & React.ComponentPropsWithoutRef<"button">
>(
  (
    {
      selectedOptions,
      isOpen,
      isDisabled,
      placeholder,
      label,
      className,
      ...props
    },
    ref
  ) => {
    // Described by the field's helper text and error
    const { "aria-describedby": ariaDescribedBy } = useFormControlInputA11y();
    const [firstSelected] = selectedOptions;
    const hasMultiple = selectedOptions.length > 1;
    // Options may carry no description, so the second line is dropped rather
    // than rendered empty, which would leave the trigger unevenly padded.
    const secondaryLine = hasMultiple
      ? selectedOptions.map((option) => option.label).join(", ")
      : firstSelected?.description;

    return (
      <button
        {...props}
        aria-describedby={ariaDescribedBy}
        ref={ref}
        type="button"
        disabled={isDisabled}
        className={cn(
          // Mirrors the select inputs' trigger so the control lines up with the
          // rest of the form: the wrapper stays unstyled and the border lives here.
          "flex min-h-[42px] w-full cursor-pointer items-center gap-3 rounded-md border border-input bg-background px-3 py-2 text-left text-sm transition-colors",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          "disabled:!bg-secondary disabled:cursor-not-allowed disabled:text-neutral-400 disabled:opacity-60",
          className
        )}
      >
        {label && <span className="sr-only">{label}: </span>}
        {firstSelected ? (
          <>
            <ImageThumbnail src={firstSelected.imageUrl} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium text-neutral-900 text-sm">
                {hasMultiple
                  ? `${selectedOptions.length} selected`
                  : firstSelected.label}
              </span>
              {secondaryLine ? (
                <span className="block truncate text-content text-sm">
                  {secondaryLine}
                </span>
              ) : null}
            </span>
          </>
        ) : (
          <span className="flex-1 truncate text-content">{placeholder}</span>
        )}
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-neutral-500 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>
    );
  }
);
ImageOptionPickerTrigger.displayName = "ImageOptionPickerTrigger";
